import React from "react";

// Lightweight markdown renderer for AI legal responses.
function inline(text, keyBase) {
  const parts = [];
  const regex = /(\*\*[^*]+\*\*|\*[^*\s][^*]*\*|`[^`]+`|\[[^\]]+\]\(https?:\/\/[^)\s]+\))/g;
  let last = 0;
  let m;
  let i = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const token = m[0];
    if (token.startsWith("*") && !token.startsWith("**")) {
      parts.push(<em key={`${keyBase}-i-${i}`} className="italic">{token.slice(1, -1)}</em>);
    } else if (token.startsWith("**")) {
      parts.push(
        <strong key={`${keyBase}-b-${i}`} className="font-semibold text-foreground">
          {token.slice(2, -2)}
        </strong>
      );
    } else if (token.startsWith("[")) {
      const [, label, href] = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      const cite = /^[WS]?\d+$/.test(label);
      parts.push(
        <a key={`${keyBase}-a-${i}`} href={href} target="_blank" rel="noreferrer" data-testid={cite ? "inline-citation" : undefined}
          className={cite
            ? "inline-flex items-center align-super text-[10px] font-mono px-1 mx-0.5 border border-brand/40 text-brand hover:bg-brand/10 transition-colors"
            : "text-brand underline underline-offset-2"}>
          {label}
        </a>
      );
    } else if (token.startsWith("`")) {
      parts.push(
        <code key={`${keyBase}-c-${i}`} className="font-mono text-[0.85em] px-1 py-0.5 rounded bg-muted">
          {token.slice(1, -1)}
        </code>
      );
    }
    last = m.index + token.length;
    i++;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

export default function Markdown({ content, className = "" }) {
  if (!content) return null;
  const lines = content.split("\n");
  const blocks = [];
  let list = [];

  const flush = (key) => {
    if (list.length) {
      blocks.push(
        <ul key={`ul-${key}`} className="list-disc pl-5 space-y-1.5 my-3">
          {list.map((item, idx) => (
            <li key={idx} className="leading-relaxed">{inline(item, `li-${key}-${idx}`)}</li>
          ))}
        </ul>
      );
      list = [];
    }
  };

  lines.forEach((raw, idx) => {
    const line = raw.trim();
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(line)) {
      flush(idx);
      blocks.push(<hr key={idx} className="my-5 border-border" />);
    } else if (/^###\s+/.test(line)) {
      flush(idx);
      blocks.push(<h4 key={idx} className="font-serif text-base font-semibold mt-4 mb-1.5">{inline(line.replace(/^###\s+/, ""), `h4-${idx}`)}</h4>);
    } else if (/^##\s+/.test(line)) {
      flush(idx);
      blocks.push(<h3 key={idx} className="font-serif text-lg font-semibold mt-5 mb-2">{inline(line.replace(/^##\s+/, ""), `h3-${idx}`)}</h3>);
    } else if (/^#\s+/.test(line)) {
      flush(idx);
      blocks.push(<h2 key={idx} className="font-serif text-xl font-semibold mt-5 mb-2">{inline(line.replace(/^#\s+/, ""), `h2-${idx}`)}</h2>);
    } else if (/^[-*]\s+/.test(line)) {
      list.push(line.replace(/^[-*]\s+/, ""));
    } else if (/^\d+\.\s+/.test(line)) {
      list.push(line.replace(/^\d+\.\s+/, ""));
    } else if (line === "") {
      flush(idx);
    } else {
      flush(idx);
      blocks.push(<p key={idx} className="leading-relaxed my-2">{inline(line, `p-${idx}`)}</p>);
    }
  });
  flush("end");

  return <div className={`text-sm text-card-foreground/90 ${className}`}>{blocks}</div>;
}
